import { getDate } from "@miplatform/utils";
import { Box, useTheme } from "@mui/material";
import {
  DesktopDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";
import { DatePickerActionBar } from "./DatePickerActionBar";

/**
 * DatePicker component for selecting dates with advanced customization options.
 *
 * @param {Object} props - Component props
 * @param {string} [props.error] - If true, displays error state and helper text
 * @param {function} props.onChange - Callback fired when the value changes
 * @param {*} props.value - The current value of the picker (date or string)
 * @param {string} props.label - The label for the input field
 * @param {React.ReactNode} [props.startAdornment] - Element to display at the start of the input
 * @param {React.ReactNode} [props.endAdornment] - Element to display at the end of the input
 * @param {function} [props.onOpen] - Callback fired when the picker is opened
 * @param {function} [props.onClose] - Callback fired when the picker is closed
 * @param {boolean} [props.open] - Controls the open state of the picker
 * @param {Array<string>} [props.actions] - Actions to display in the action bar
 * @param {Object} [props.slotProps] - Props for customizing internal slots
 * @param {Object} [props.slots] - Custom slot components
 * @param {boolean} [props.isFullWidth] - If true, the input takes full width
 * @param {boolean} [props.required=false] - If true, the field is required
 * @param {function} [props.onBlur] - If true, the field is required
 * @param {boolean} [props.whiteBackground] - If true, the field has a white background
 * @param {Object} [props.rest] - Additional props passed to DesktopDatePicker
 * @returns {JSX.Element}
 */
export const DatePicker = ({
  error = null,
  onChange,
  value,
  label,
  startAdornment,
  endAdornment,
  onOpen,
  onClose,
  open,
  actions,
  slotProps,
  slots,
  isFullWidth,
  isShrink,
  required = false,
  onBlur,
  whiteBackground = false,
  readOnly,
  disablePast,
  ...rest
}) => {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box style={{ display: "flex", flexDirection: "row" }} minWidth={140}>
        {startAdornment}
        <DesktopDatePicker
          inline
          label={label}
          format="MM/DD/YY"
          error={error}
          onOpen={onOpen}
          onClose={onClose}
          open={open}
          value={value ? getDate(value) : null}
          onChange={onChange}
          onBlur={onBlur}
          disablePast={disablePast}
          slots={{
            actionBar: DatePickerActionBar,
            ...slots,
          }}
          slotProps={{
            actionBar: {
              actions,
              ...(slotProps?.actionBar || {}),
            },
            textField: {
              size: "small",
              label,
              required,
              inputProps: {
                "data-testid": "date-picker-input",
                readOnly,
              },
              error: !!error,
              helperText: error,
              InputLabelProps: {
                shrink: isShrink,
              },
              sx: {
                width: isFullWidth ? "100%" : rest?.width || "100%",
                "& .MuiInputBase-root": {
                  padding: "0px 8px",
                  background: whiteBackground
                    ? theme.palette.common.white
                    : "inherit",
                  ...(rest.readOnly && {
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },

                    "& .MuiInputAdornment-root": {
                      display: "none",
                    },
                  }),
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8.5px",
                },
              },
            },
            error,
            ...slotProps,
          }}
          {...rest}
        />
        {endAdornment}
      </Box>
    </LocalizationProvider>
  );
};
